import React, { useState, useEffect } from 'react';
import { Check, Plus } from 'lucide-react';
import { cn } from '../../lib/utils'; // Assuming utils exists, otherwise I'll inline helper
import { Input } from './input';
import { Button } from './button';

interface SelectablePillsProps {
    options: string[] | { label: string; value: string }[];
    value: string | string[];
    onChange: (value: any) => void;
    multi?: boolean;
    label?: string;
    className?: string;
    allowCustom?: boolean; // For "Other" scenarios
    maxSelections?: number;
}

export function SelectablePills({
    options,
    value,
    onChange,
    multi = false,
    label,
    className,
    allowCustom = false,
    maxSelections,
}: SelectablePillsProps) {
    const [customValue, setCustomValue] = useState('');
    const [isTypingCustom, setIsTypingCustom] = useState(false);

    // Normalize options to object format
    const normalizedOptions = options.map((opt) =>
        typeof opt === 'string' ? { label: opt, value: opt } : opt
    );

    const isSelected = (val: string) => {
        if (multi && Array.isArray(value)) {
            return value.includes(val);
        }
        return value === val;
    };

    const handleSelect = (val: string) => {
        if (multi) {
            const currentValues = Array.isArray(value) ? [...value] : [];
            if (currentValues.includes(val)) {
                onChange(currentValues.filter((v) => v !== val));
            } else {
                if (maxSelections && currentValues.length >= maxSelections) return;
                onChange([...currentValues, val]);
            }
        } else {
            onChange(val);
        }
        setIsTypingCustom(false);
    };

    const handleCustomSubmit = () => {
        if (!customValue.trim()) return;
        handleSelect(customValue);
        setCustomValue('');
        setIsTypingCustom(false);
    };

    return (
        <div className={cn('space-y-3', className)}>
            {label && <label className="text-sm font-medium text-gray-700">{label}</label>}
            <div className="flex flex-wrap gap-2">
                {normalizedOptions.map((option) => {
                    const selected = isSelected(option.value);
                    return (
                        <button
                            key={option.value}
                            type="button"
                            onClick={() => handleSelect(option.value)}
                            className={cn(
                                'inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border',
                                selected
                                    ? 'bg-rose-500 border-rose-500 text-white shadow-md transform scale-105'
                                    : 'bg-white border-gray-200 text-gray-700 hover:bg-rose-50 hover:border-rose-200 hover:text-rose-600'
                            )}
                        >
                            {selected && <Check className="w-3.5 h-3.5 mr-1.5" />}
                            {option.label}
                        </button>
                    );
                })}

                {allowCustom && (
                    <div className="relative">
                        {isTypingCustom ? (
                            <div className="flex items-center animate-in fade-in zoom-in duration-200">
                                <Input
                                    autoFocus
                                    value={customValue}
                                    onChange={(e) => setCustomValue(e.target.value)}
                                    onBlur={handleCustomSubmit}
                                    onKeyDown={(e) => e.key === 'Enter' && handleCustomSubmit()}
                                    className="h-9 w-32 rounded-l-full rounded-r-none border-r-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                                    placeholder="Type..."
                                />
                                <Button
                                    type="button"
                                    size="sm"
                                    onClick={handleCustomSubmit}
                                    className="h-9 rounded-r-full rounded-l-none bg-rose-500 hover:bg-rose-600 text-white px-3"
                                >
                                    <Plus className="w-4 h-4" />
                                </Button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setIsTypingCustom(true)}
                                className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border border-dashed border-gray-300 text-gray-500 hover:bg-rose-50 hover:border-rose-300 hover:text-rose-600"
                            >
                                <Plus className="w-3.5 h-3.5 mr-1.5" />
                                Other
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
